import { useState, useEffect } from 'react';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { fetchActiveSchools } from '../ApiHandler/schoolFunctions';
import { fetchLabsForSchool } from '../ApiHandler/labFunctions';
import { fetchActiveEquipments, handleAllocateEquipment, handleUpdateAllocation } from '../ApiHandler/equipmentFunctions';

const EquipmentAllocation = ({ editFormData, handleClose }) => {
    const isEditing = !!editFormData;
    const [equipmentData, setEquipmentData] = useState({
        equipmentId: editFormData ? editFormData.equipment_id || "" : "",
        equipmentName: editFormData ? editFormData.equipment_name || "" : "",
        schoolId: editFormData ? editFormData.school_id || "" : "",
        schoolName: editFormData ? editFormData.school_name || "" : "",
        labId: editFormData ? editFormData.lab_id || "" : "",
        labName: editFormData ? editFormData.lab_name || "" : "",
        allocationDate: editFormData ? editFormData.allocation_date || "" : "",
        allocatedQuantity: editFormData ? editFormData.allocated_quantity || "" : "",
    });

    const [equipments, setEquipments] = useState([]);
    const [schools, setSchools] = useState([]);
    const [labs, setLabs] = useState([]);
    const [availableQuantity, setAvailableQuantity] = useState("");
    const [selectedSchools, setSelectedSchools] = useState([]);
    const [selectedLabs, setSelectedLabs] = useState([]);
    const [selectedEquipments, setSelectedEquipments] = useState([]);
    const [schoolSearchTerm, setSchoolSearchTerm] = useState("");
    const [equipmentSearchTerm, setEquipmentSearchTerm] = useState("");

    
    const handleFormSubmit = async (e) => {
        e.preventDefault();
        if (isEditing) {
            const mappedData = {
                allocated_quantity: equipmentData.allocatedQuantity
            };
            await handleUpdateAllocation(e, editFormData.id, mappedData);
            if (handleClose) handleClose();
        } else {
            handleAllocateEquipment(e, equipmentData, setEquipmentData);
        }
    };

    const handleSchoolCheckbox = async (schoolId) => {
        let updatedSelected;

        if (selectedSchools.includes(schoolId)) {
            updatedSelected = selectedSchools.filter(id => id !== schoolId);
        } else {
            updatedSelected = [...selectedSchools, schoolId];
        }
        setSelectedSchools(updatedSelected);

        const selectedSchoolObjects = schools.filter(s => updatedSelected.includes(s.id));
        const selectedIds = selectedSchoolObjects.map(s => s.id);
        const selectedNames = selectedSchoolObjects.map(s => s.school_name);

        setEquipmentData(prevData => ({
            ...prevData,
            schoolId: selectedIds.join(','),
            schoolName: selectedNames.join(', ')
        }));

        if (updatedSelected.length > 0) {
            try {
                const token = localStorage.getItem("token");
                const promises = updatedSelected.map(id => 
                    fetch(`${process.env.REACT_APP_API_URL}/labs/labs-for-school/${id}`, {
                        headers: { Authorization: token }
                    }).then(res => res.json())
                );
                const responses = await Promise.all(promises);
                let allLabs = [];
                responses.forEach(res => {
                    if (res.status === 'success' && res.labs) {
                        allLabs = [...allLabs, ...res.labs];
                    }
                });
                const uniqueLabs = Array.from(new Map(allLabs.map(lab => [lab.id, lab])).values());
                setLabs(uniqueLabs);
            } catch (error) {
                console.error("Error fetching labs for multiple schools:", error);
            }
        } else {
            setLabs([]);
        }
    };

    const handleLabCheckbox = (labId) => {
        let updatedSelected;

        if (selectedLabs.includes(labId)) {
            updatedSelected = selectedLabs.filter(id => id !== labId);
        } else {
            updatedSelected = [...selectedLabs, labId];
        }

        setSelectedLabs(updatedSelected);

        const selectedLabObjects = labs.filter(lab => updatedSelected.includes(lab.id));
        const selectedIds = selectedLabObjects.map(lab => lab.id);
        const selectedNames = selectedLabObjects.map(lab => lab.lab_name);

        setEquipmentData(prevData => ({
            ...prevData,
            labId: selectedIds.join(','),
            labName: selectedNames.join(', ')
        }));
    };

    const handleEquipmentCheckbox = (equipmentId) => {
        let updatedSelected;

        if (selectedEquipments.includes(equipmentId)) {
            updatedSelected = selectedEquipments.filter(id => id !== equipmentId);
        } else {
            updatedSelected = [...selectedEquipments, equipmentId];
        }

        setSelectedEquipments(updatedSelected);

        const selectedEquipmentObjects = equipments.filter(eq => updatedSelected.includes(eq.id));
        const selectedIds = selectedEquipmentObjects.map(eq => eq.id);
        const selectedNames = selectedEquipmentObjects.map(eq => eq.equipment_name);
        
        let minAvailable = 0;
        if (selectedEquipmentObjects.length > 0) {
            minAvailable = Math.min(...selectedEquipmentObjects.map(eq => eq.available_quantity));
        }

        setAvailableQuantity(minAvailable);
        setEquipmentData(prevData => ({
            ...prevData,
            equipmentId: selectedIds.join(','),
            equipmentName: selectedNames.join(', ')
        }));
    };

    const handleChange = (e) => {
        const { name, value } = e.target;

        if (name === "allocatedQuantity") {
            const quantity = parseInt(value);
            if (quantity > availableQuantity) {
                toast.warning(`The maximum available quantity is ${availableQuantity}`, {
                    position: "top-center"
                });
                setEquipmentData(prevData => ({
                    ...prevData,
                    [name]: availableQuantity
                }));
            } else {
                setEquipmentData(prevData => ({
                    ...prevData,
                    [name]: quantity
                }));
            }
        } else {
            setEquipmentData(prevData => ({
                ...prevData,
                [name]: value
            }));
        }
    };

    useEffect(() => {
        fetchActiveEquipments(setEquipments);
        fetchActiveSchools(setSchools);
    }, []);

    useEffect(() => {
        if (editFormData) {
            if (editFormData.school_id) {
                setSelectedSchools([editFormData.school_id]);
                fetchLabsForSchool(editFormData.school_id, setLabs);
            }
            if (editFormData.lab_id) {
                setSelectedLabs([editFormData.lab_id]);
            }
            if (editFormData.equipment_id) {
                setSelectedEquipments([editFormData.equipment_id]);
                // Set available quantity for display
                const selectedEq = equipments.find(eq => eq.id === editFormData.equipment_id);
                if (selectedEq) setAvailableQuantity(selectedEq.available_quantity);
            }
        }
    }, [editFormData, equipments]);

    return (
        <div className="upload-document-container">
            <ToastContainer />
            <header className="upload-document-header">
                <h1>{isEditing ? 'Edit Allocated Equipment Data' : 'Equipment Allocation'}</h1>
            </header>
            <form className="upload-document-form" onSubmit={handleFormSubmit}>
                <div className='in-row-input'>
                    <div className="form-group">
                        <label>School Name <m style={{ color: 'red' }}>*</m></label>
                        <div style={{
                            border: '1px solid #ccc',
                            borderRadius: '4px',
                            padding: '10px',
                            maxHeight: '200px',
                            overflowY: 'auto',
                            backgroundColor: '#fff'
                        }}>
                            <input 
                                type="text" 
                                placeholder="Search school..." 
                                value={schoolSearchTerm}
                                onChange={(e) => setSchoolSearchTerm(e.target.value)}
                                style={{ width: '100%', padding: '5px', marginBottom: '10px', border: '1px solid #ccc', borderRadius: '4px' }}
                            />
                            {schools.filter(s => s.school_name.toLowerCase().includes(schoolSearchTerm.toLowerCase())).length > 0 ? (
                                schools.filter(s => s.school_name.toLowerCase().includes(schoolSearchTerm.toLowerCase())).map((school) => (
                                    <div key={school.id} style={{
                                        padding: '8px',
                                        cursor: 'pointer',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '10px'
                                    }}>
                                        <input
                                            type="checkbox"
                                            id={`school-${school.id}`}
                                            checked={selectedSchools.includes(school.id)}
                                            onChange={() => handleSchoolCheckbox(school.id)}
                                            style={{ cursor: 'pointer', width: '18px', height: '18px' }}
                                        />
                                        <label
                                            htmlFor={`school-${school.id}`}
                                            style={{ cursor: 'pointer', margin: 0, flex: 1 }}
                                        >
                                            {school.school_name}
                                        </label>
                                    </div>
                                ))
                            ) : (
                                <p style={{ color: '#999', margin: 0 }}>No schools available</p>
                            )}
                        </div>
                        <small style={{ color: '#666', fontSize: '12px' }}>
                            {selectedSchools.length} school(s) selected
                        </small>
                    </div>
                    <div className="form-group">
                        <label>Lab Name <m style={{ color: 'red' }}>*</m></label>
                        <div style={{
                            border: '1px solid #ccc',
                            borderRadius: '4px',
                            padding: '10px',
                            maxHeight: '200px',
                            overflowY: 'auto',
                            backgroundColor: '#fff'
                        }}>
                            {labs.length > 0 ? (
                                labs.map((lab) => (
                                    <div key={lab.id} style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '5px' }}>
                                        <input 
                                            type="checkbox" 
                                            id={`lab-${lab.id}`}
                                            checked={selectedLabs.includes(lab.id)}
                                            onChange={() => handleLabCheckbox(lab.id)}
                                            style={{ cursor: 'pointer', width: '18px', height: '18px' }}
                                        />
                                        <label
                                            htmlFor={`lab-${lab.id}`}
                                            style={{ cursor: 'pointer', margin: 0, flex: 1 }}
                                        >
                                            {lab.lab_name}
                                        </label>
                                    </div>
                                ))
                            ) : (
                                <p style={{ color: '#999', margin: 0 }}>No labs available</p>
                            )}
                        </div>
                        <small style={{ color: '#666', fontSize: '12px' }}>
                            {selectedLabs.length} lab(s) selected
                        </small>
                    </div>
                </div>
                <div className='in-row-input'>
                    <div className="form-group">
                        <label>Equipment Name <m style={{ color: 'red' }}>*</m></label>
                        <div style={{
                            border: '1px solid #ccc',
                            borderRadius: '4px',
                            padding: '10px',
                            maxHeight: '200px',
                            overflowY: 'auto',
                            backgroundColor: '#fff'
                        }}>
                            <input 
                                type="text" 
                                placeholder="Search equipment..." 
                                value={equipmentSearchTerm}
                                onChange={(e) => setEquipmentSearchTerm(e.target.value)}
                                style={{ width: '100%', padding: '5px', marginBottom: '10px', border: '1px solid #ccc', borderRadius: '4px' }}
                            />
                            {equipments.filter(e => e.equipment_name.toLowerCase().includes(equipmentSearchTerm.toLowerCase())).length > 0 ? (
                                equipments.filter(e => e.equipment_name.toLowerCase().includes(equipmentSearchTerm.toLowerCase())).map((equipment) => (
                                    <div key={equipment.id} style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '8px' }}>
                                        <input
                                            type="checkbox"
                                            id={`equipment-${equipment.id}`}
                                            checked={selectedEquipments.includes(equipment.id)}
                                            onChange={() => handleEquipmentCheckbox(equipment.id)}
                                            disabled={equipment.available_quantity <= 0}
                                            style={{ cursor: equipment.available_quantity <= 0 ? 'not-allowed' : 'pointer', width: '18px', height: '18px' }}
                                        />
                                        <label
                                            htmlFor={`equipment-${equipment.id}`}
                                            style={{ cursor: equipment.available_quantity <= 0 ? 'not-allowed' : 'pointer', margin: 0, flex: 1, color: equipment.available_quantity <= 0 ? '#aaa' : '#000' }}
                                        >
                                            {equipment.equipment_name} (Available: {equipment.available_quantity})
                                        </label>
                                    </div>
                                ))
                            ) : (
                                <p style={{ color: '#999', margin: 0 }}>No equipment available</p>
                            )}
                        </div>
                        <small style={{ color: '#666', fontSize: '12px' }}>
                            {selectedEquipments.length} equipment(s) selected
                        </small>
                    </div>
                    <div className="form-group">
                        <label>Allocation Date <m style={{ color: 'red' }}>*</m></label>
                        <input
                            type="date"
                            name="allocationDate"
                            value={equipmentData.allocationDate}
                            onChange={handleChange}
                            required
                        />
                    </div>
                </div>
                <div className='in-row-input'>
                    <div className="form-group">
                        <label>Available Quantity</label>
                        <input
                            type="number"
                            value={availableQuantity}
                            placeholder="Available Quantity"
                            readOnly
                        />
                    </div>
                    <div className="form-group">
                        <label>Allocation Quantity <m style={{ color: 'red' }}>*</m></label>
                        <input
                            type="number"
                            name="allocatedQuantity"
                            value={equipmentData.allocatedQuantity}
                            onChange={(e) => {
                                const value = e.target.value;
                                if (/^\d*$/.test(value) && value <= 9999) {
                                    handleChange(e);
                                }
                            }}
                            placeholder="Enter Quantity to Allocate"
                            required
                        />
                    </div>
                </div>
                <div className="form-group">
                    <button type="submit">Submit</button>
                </div>
            </form>
        </div>
    );
}

export default EquipmentAllocation;